import { useState, useEffect } from 'react'
import type { FormEvent } from 'react'
import { motion } from 'framer-motion'
import { Box, Container, Paper, Typography } from '@mui/material'
import DonationForm from './DonationForm'
import RecentDonations from './RecentDonations'
import type { FormData, FormErrors, Donation } from './types'
import { STORAGE_KEY } from './types'


function Form() {
  const [formData, setFormData] = useState<FormData>({
    currency: 'USD',
    amount: '',
    email: '',
    paymentMethod: 'ecocash',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [recentDonations, setRecentDonations] = useState<Donation[]>([])

  // Load donations from localStorage on mount
useEffect(() => {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (!stored) return

  type StoredDonation = Omit<Donation, 'date'> & {
    date: string
  }

  try {
    const donations = (JSON.parse(stored) as StoredDonation[]).map(
      (d): Donation => ({
        ...d,
        date: new Date(d.date),
      })
    )

    setRecentDonations(donations)
  } catch (error) {
    console.error('Error loading donations from storage:', error)
  }
}, [])


  // Save donations to localStorage whenever they change
  useEffect(() => {
    if (recentDonations.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recentDonations))
    }
  }, [recentDonations])

  const validateEmail = (email: string): boolean => {
    if (!email) return true // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateAmount = (amount: string): boolean => {
    if (!amount.trim()) return false
    const numAmount = parseFloat(amount)
    return !isNaN(numAmount) && numAmount > 0
  }

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const newErrors: FormErrors = {}

    if (!validateAmount(formData.amount)) {
      newErrors.amount = 'Please enter a valid donation amount'
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)

    // Simulate API call - replace with actual Paynow integration
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Create new donation entry
      const newDonation: Donation = {
        id: Date.now().toString(),
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        email: formData.email.trim(),
        paymentMethod: formData.paymentMethod,
        status: 'Pending',
        date: new Date(),
      }

      // Add to recent donations (prepend to show newest first)
      setRecentDonations((prev) => [newDonation, ...prev])

      // Reset form
      setFormData({
        currency: 'USD',
        amount: '',
        email: '',
        paymentMethod: 'ecocash',
      })

      //  Paynow Zimbabwe API
      console.log('Donation data:', formData)
      // Note: l will replace this alert with actual Paynow payment URL redirection like below
      // window.location.href = paynowUrl
    } catch (error) {
      console.error('Error processing donation:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
        py: { xs: 6, sm: 8 },
        px: { xs: 2, sm: 3, lg: 4 },
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Support Us with a Donation
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mx: 'auto', maxWidth: 600 }}>
              Your generous contribution helps us continue providing compassionate services to families across Zimbabwe and beyond.
            </Typography>
          </Box>
        </motion.div>

        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2 }}>
          <DonationForm
            formData={formData}
            errors={errors}
            isSubmitting={isSubmitting}
            onInputChange={handleInputChange}
            onSubmit={handleSubmit}
          />
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <RecentDonations donations={recentDonations} />
        </Paper>
      </Container>
    </Box>
  )
}

export default Form
